import * as React from 'react';
import { action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { BookingCancellationRefundDto, computeSelectedTotalForMethod, RefundModel } from './NewCancelBookingModal';

interface RefundMethodSelectorProps {
	bookings: BookingCancellationRefundDto[];
	refundModel: RefundModel;
	onMethodChange?: () => void;
}

export const RefundMethodSelector = observer(({ bookings, refundModel, onMethodChange }: RefundMethodSelectorProps) => {
	const bankTotal = computeSelectedTotalForMethod(bookings, refundModel.selectedBookings, false);
	const creditTotal = computeSelectedTotalForMethod(bookings, refundModel.selectedBookings, true);

	const selectMethod = action((forceCreditRefund: boolean) => {
		if (refundModel.forceCreditRefund === forceCreditRefund) return;
		runInAction(() => {
			refundModel.forceCreditRefund = forceCreditRefund;
			// Reset refund amount + clear any staff override error so the displayed
			// total matches the newly-selected method.
			refundModel.refundAmount = computeSelectedTotalForMethod(
				bookings,
				refundModel.selectedBookings,
				forceCreditRefund,
			);
			refundModel.refundAmountErrors = '';
		});
		onMethodChange?.();
	});

	return (
		<div className="refund-method-selector">
			<p className="input-label">Refund method</p>
			<div className="refund-method-selector__options">
				<MethodOption
					selected={!refundModel.forceCreditRefund}
					iconName="account_balance"
					iconColor="blue"
					title="Bank refund"
					description="Refund to original payment method"
					amount={bankTotal}
					onClick={() => selectMethod(false)}
				/>
				<MethodOption
					selected={refundModel.forceCreditRefund}
					iconName="redeem"
					iconColor="orange"
					title="Credit voucher"
					description="Issue a credit voucher instead"
					amount={creditTotal}
					onClick={() => selectMethod(true)}
				/>
			</div>
		</div>
	);
});

interface MethodOptionProps {
	selected: boolean;
	iconName: string;
	iconColor: 'blue' | 'orange';
	title: string;
	description: string;
	amount: number;
	onClick: () => void;
}

function MethodOption({ selected, iconName, iconColor, title, description, amount, onClick }: MethodOptionProps) {
	return (
		<button
			type="button"
			className={`refund-method-option ${selected ? 'refund-method-option--selected' : ''}`}
			onClick={onClick}
			aria-pressed={selected}
		>
			<div className={`refund-method-option__icon refund-method-option__icon--${iconColor}`}>
				<Icon name={iconName} useMaterialIcons />
			</div>
			<div className="refund-method-option__content">
				<p className="refund-method-option__title">{title}</p>
				<p className="refund-method-option__description">{description}</p>
			</div>
			<p className="refund-method-option__amount">{formatCurrency(amount)}</p>
		</button>
	);
}
